import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';

const POETIC_LINES = [
  '正在唤醒神经核心',
  '编织记忆星图',
  '校准星轨矩阵',
  '点亮认知节点',
  '链接虚拟桌面',
];

const FALLING_LEAVES = [
  { left: '12%', delay: 0, dur: 9, size: 'text-2xl' },
  { left: '78%', delay: 2.5, dur: 11, size: 'text-xl' },
  { left: '45%', delay: 4, dur: 10, size: 'text-lg' },
  { left: '63%', delay: 6, dur: 12, size: 'text-2xl' },
];

export function HardcoreBootSequence({ onComplete, t }: { onComplete: () => void; t?: any }) {
  const [progress, setProgress] = useState(0);
  const [lines, setLines] = useState<string[]>([]);
  const [phase, setPhase] = useState<'loading' | 'ready'>('loading');
  // v3.12.2: ref 持有 onComplete 避免 effect 闭包陈旧 + 重启动画
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    let current = 0;
    let timer = window.setTimeout(function tick() {
      if (current < POETIC_LINES.length) {
        setLines(prev => [...prev, POETIC_LINES[current]]);
        setProgress(Math.round(((current + 1) / (POETIC_LINES.length + 1)) * 100));
        current++;
        timer = window.setTimeout(tick, 520);
      } else {
        setProgress(100);
        setPhase('ready');
        timer = window.setTimeout(() => onCompleteRef.current(), 1100);
      }
    }, 220);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
      className="fixed inset-0 z-[9999] bg-[#05050a] overflow-hidden flex items-center justify-center"
    >
      {/* 背景星辉 + 飘落枫叶 */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[22%] left-[22%] w-[480px] h-[480px] bg-celestial-saturn/10 blur-[140px] rounded-full animate-pulse" />
        <div className="absolute bottom-[20%] right-[22%] w-[420px] h-[420px] bg-celestial-saturn/8 blur-[120px] rounded-full" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,204,0,0.06),transparent_60%)]" />
        {FALLING_LEAVES.map((l, i) => (
          <motion.div
            key={i}
            className={`absolute top-[-10%] ${l.size} text-celestial-saturn/25`}
            style={{ left: l.left }}
            initial={{ y: '-10%', opacity: 0, rotate: 0 }}
            animate={{ y: '110vh', opacity: [0, 0.55, 0.55, 0], rotate: 360 }}
            transition={{ duration: l.dur, delay: l.delay, repeat: Infinity, ease: 'linear' }}
          >
            🍁
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 flex flex-col items-center w-[min(92vw,520px)] px-6">
        {/* 🍁 枫叶品牌标识 + 轨道环 */}
        <div className="relative w-40 h-40 mb-10 flex items-center justify-center">
          <motion.div
            className="absolute inset-0 rounded-full border border-celestial-saturn/20"
            animate={{ rotate: 360 }}
            transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute inset-4 rounded-full border border-celestial-saturn/15"
            animate={{ rotate: -360 }}
            transition={{ duration: 9, repeat: Infinity, ease: 'linear' }}
          />
          <div className="absolute inset-0 rounded-full blur-2xl bg-celestial-saturn/20 animate-pulse" />
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            className="relative w-24 h-24 rounded-3xl bg-gradient-to-br from-celestial-saturn to-amber-500/70 flex items-center justify-center shadow-[0_0_50px_rgba(255,204,0,0.5)] border border-white/20"
          >
            <motion.span
              className="text-5xl leading-none drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
              animate={{ rotate: [0, 8, -6, 0], scale: [1, 1.08, 1] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            >
              🍁
            </motion.span>
          </motion.div>
        </div>

        {/* 标题 */}
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-3xl font-black tracking-[0.3em] text-white/90 mb-2"
        >
          小枫<span className="text-celestial-saturn text-glow"> OS</span>
        </motion.h1>
        <p className="text-xs uppercase tracking-[0.4em] text-white/35 mb-8">{t?.bootSubtitle || '智能神经操作系统'}</p>

        {/* 诗意加载语 */}
        <div className="h-6 mb-6">
          <AnimatePresence mode="wait">
            {lines.length > 0 && (
              <motion.p
                key={lines.length}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="text-sm font-medium tracking-widest text-celestial-saturn/90"
              >
                {lines[lines.length - 1]}…
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* 玻璃质感进度条 + 流动金光 + 行进枫叶标记 */}
        <div className="relative w-full">
          <div className="relative w-full h-2 rounded-full bg-white/5 border border-celestial-saturn/10 overflow-hidden backdrop-blur-sm">
            <motion.div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-celestial-saturn/50 via-celestial-saturn to-amber-300 shadow-[0_0_16px_rgba(255,204,0,0.7)]"
              animate={{ width: `${progress}%` }}
              transition={{ ease: 'easeOut', duration: 0.4 }}
            />
            <motion.div
              className="absolute top-0 bottom-0 w-16 bg-gradient-to-r from-transparent via-white/50 to-transparent"
              style={{ filter: 'blur(1px)' }}
              animate={{ left: ['-20%', '120%'] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>
          <motion.div
            className="absolute -top-6 -translate-x-1/2 text-xl drop-shadow-[0_0_8px_rgba(255,204,0,0.9)]"
            animate={{ left: `${progress}%` }}
            transition={{ ease: 'easeOut', duration: 0.4 }}
          >
            🍁
          </motion.div>
        </div>
        <div className="mt-5 text-xs font-bold tracking-widest text-white/40">{t?.bootProgress || '启动进度'} {progress}%</div>
      </div>

      {/* 完成闪白 */}
      <AnimatePresence>
        {phase === 'ready' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.9, 0] }}
            transition={{ duration: 0.9 }}
            className="absolute inset-0 bg-white z-[10000]"
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
